import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { FEATURES_CONFIG, type FeatureKey } from '@/config/features';

/**
 * Exposes a helper object to the window for developer debugging.
 * Only works in non-production environments.
 */
export function initFeatureFlagDebug() {
  if (import.meta.env.MODE === 'production') return;

  const { isEnabled, setOverride, clearOverrides, overrides } = useFeatureFlags();

  (window as any).gaFeatures = {
    /**
     * List all available features and their current status
     */
    list: () => {
      console.table(
        Object.keys(FEATURES_CONFIG).map((key) => {
          const featureKey = key as FeatureKey;
          return {
            Feature: featureKey,
            Enabled: isEnabled(featureKey),
            Default: FEATURES_CONFIG[featureKey][import.meta.env.MODE as 'development' | 'staging' | 'production'] ?? false,
            Override: overrides.value[featureKey] ?? 'None',
          };
        })
      );
    },

    /**
     * Enable a feature locally
     */
    enable: (key: FeatureKey) => {
      setOverride(key, true);
      console.log(`Feature [${key}] enabled locally. (Reload may be required for some components)`);
    },

    /**
     * Disable a feature locally
     */
    disable: (key: FeatureKey) => {
      setOverride(key, false);
      console.log(`Feature [${key}] disabled locally.`);
    },

    /**
     * Clear an override and return to environment default
     */
    reset: (key: FeatureKey) => {
      setOverride(key, undefined);
      console.log(`Feature [${key}] reset to default.`);
    },

    /**
     * Clear all overrides
     */
    resetAll: () => {
      clearOverrides();
      console.log('All feature overrides cleared.');
    }
  };

  console.log('%c[GAIA] Feature Flag Debug Bridge initialized. Type "gaFeatures.list()" to see status.', 'color: #004C9C; font-weight: bold;');
}
